/**
 * Session management API schemas
 */

import { z, ZodType } from 'zod';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    DatabaseSessionData,
    DatabaseSessionCookie,
    SessionsSearchRequest,
} from '../session.types';
import { SearchTermsSchema } from './omnisearch.schemas';

export const GuestTokenSessionSnapshotSchema = z.object({
    single_use_auth_token_id: z.number().int().positive(),
    token_type: z.literal('PAYMENT_REQUEST_ACCESS'),
    payment_request_id: z.number().int().positive(),
    payment_request_uuid: z.string().uuid(),
    expires_at: z.string().datetime(),
});


export const DatabaseSessionCookieSchema: ZodType<DatabaseSessionCookie> = z
    .object({
        secure: z.boolean().optional(),
        httpOnly: z.boolean().optional(),
        domain: z.string().optional(),
        path: z.string().optional(),
        sameSite: z.union([z.boolean(), z.enum(['strict', 'lax', 'none'])]).optional(),
    })
    .catchall(z.unknown());

/**
 * Database session data structure
 * Note: CookieOptions from express-session is complex, so we use z.unknown() for cookie
 */
export const DatabaseSessionDataSchema: ZodType<DatabaseSessionData> = z.object({
    passport: z.object({
        user: z.number(),
    }),
    user_id: z.number().optional(),
    ip_address: z.string(),
    user_agent: z.string(),
    cookie: DatabaseSessionCookieSchema, // CookieOptions from express-session, not representable as JSON
    active_role_assignment_id: z.number().nullable(),
    session_uuid: z.string().uuid().optional(),
    active_role_assignment_uuid: z.string().uuid().nullable().optional(),
    impersonator: z.object({
        user_id: z.number(),
        role_assignment_id: z.number(),
    }).nullable(),
    enable_javascript_console: z.boolean(),
});

export const GuestTokenSessionDataSchema = z.object({
    guest_token: GuestTokenSessionSnapshotSchema,
    ip_address: z.string(),
    user_agent: z.string(),
    cookie: DatabaseSessionCookieSchema,
    enable_javascript_console: z.boolean(),
});

/**
 * Expire session request
 */
export const ExpireSessionRequestSchema = z.object({
    audit_id: z.number(),
});

export const ExpireSessionUuidRequestSchema: ZodType<import('../session.types').ExpireSessionUuidRequest> =
    z.object({
        session_uuid: z.string().uuid(),
    });

/**
 * Expire user session request (admin)
 */
export const ExpireUserSessionRequestSchema = z.object({
    session_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(),
});

/**
 * Update session console logging flag (admin)
 */
export const UpdateSessionConsoleRequestSchema = z.object({
    session_uuid: z.string().uuid(),
    enable_javascript_console: z.boolean(),
});

/**
 * Session list item
 */
export const SessionListItemSchema = z.object({
    session_uuid: z.string().uuid().optional(),
    audit_id: z.number().optional(),
    user_id: z.number().optional(),
    user_uuid: z.string().uuid().optional(),
    user_email: z.string(),
    ip_address: z.string(),
    user_agent: z.string(),
    expires_at: z.string(), // ISO date string
    active_role_assignment_id: z.number().nullable().optional(),
    active_role_assignment_uuid: z.string().uuid().nullable().optional(),
    enable_javascript_console: z.boolean(),
    is_current: z.boolean(),
});

/**
 * Get sessions response
 */
export const GetSessionsResponseSchema = z.object({
    sessions: z.array(SessionListItemSchema),
});

export const SessionsSearchRequestSchema: z.ZodType<SessionsSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: z.coerce.number().int().positive().max(500).optional(),
        cursor: z.string().trim().min(1).optional(),
    })
    .strict();

export const ListSessionsPageResponseSchema = z.object({
    sessions: z.array(SessionListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});
